import { CUSTOMER_ADDRESSES_SPACE_PARAMETER_SUPPORT } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useCounterpartyFilter, useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useCustomers, useCustomersContext } from '@src/hooks/useCustomer';
import { useCustomerMarketAccounts } from '@src/providers/CustomerMarketAccountsProvider';
import type { ICustomerAddress, ICustomerRoutingInfo } from '@talos/kyoko';
import {
  ACTION,
  Button,
  CUSTOMER_ADDRESS,
  EntityAdmin,
  FormControlSizes,
  IconName,
  useAddressRoutingTypesFilter,
  useCurrenciesContext,
  useCurrenciesFilter,
  useCustomerAddressStatusesFilter,
  useCustomerAddressTypesFilter,
  useDynamicCallback,
  useMarketAccountsContext,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { useConfirmCustomerAddressMenu } from './useConfirmCustomerAddressMenu';
import { getCustomerAddressDrawerOptions, getCustomerAddressesColumns, getCustomerAddressesPostEntity } from './utils';

export interface CustomerAddressesDrawerEntity extends Omit<ICustomerAddress, 'RoutingInfo'> {
  _WalletAddress: string;
  _Memo: string;
}

const WS_REQUEST = { name: CUSTOMER_ADDRESS, tag: 'CustomerAddresses' };

export function CustomerAddresses() {
  const { isAuthorized } = useRoleAuth();
  const customers = useCustomers();
  const { getCustomerWithdrawAddress } = useCustomersContext();
  const { currenciesList: currencies } = useCurrenciesContext();

  const [routingInfoMap] = useState<Map<string, ICustomerRoutingInfo>>(new Map());

  const statusFilter = useCustomerAddressStatusesFilter();
  const counterpartyFilter = useCounterpartyFilter();
  const currenciesFilter = useCurrenciesFilter();
  const addressTypesFilter = useCustomerAddressTypesFilter();
  const addressRoutingTypesFilter = useAddressRoutingTypesFilter();
  const availableFilterableProperties = useMemo(
    () => [statusFilter, counterpartyFilter, currenciesFilter, addressTypesFilter, addressRoutingTypesFilter],
    [addressRoutingTypesFilter, addressTypesFilter, counterpartyFilter, currenciesFilter, statusFilter]
  );
  const { externallyControlledFilter, filterableProperties } = useSpaceParameterControlledFilter(
    CUSTOMER_ADDRESSES_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );

  const { disableCustomerMarketAccounts } = useFeatureFlag();
  const { marketAccountsList } = useMarketAccountsContext();
  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  const addressMarketAccounts = disableCustomerMarketAccounts ? marketAccountsList : customerMarketAccountsList;
  const { currenciesBySymbol } = useCurrenciesContext();
  const getEntityForPatchPost = useDynamicCallback((entity: CustomerAddressesDrawerEntity) =>
    // The address routing type is appended based on the selected currency
    getCustomerAddressesPostEntity(entity, currenciesBySymbol?.get(entity.Currency))
  );

  const drawerOptions = useMemo(() => {
    if (!customers || !currencies || !addressMarketAccounts) {
      return undefined;
    }
    return getCustomerAddressDrawerOptions({ customers, currencies, marketAccounts: addressMarketAccounts });
  }, [addressMarketAccounts, customers, currencies]);

  const RevealRoutingInfoRenderer = useDynamicCallback(
    ({ data, value, api }: ICellRendererParams<ICustomerAddress, string>): React.ReactNode => {
      if (value != null || data == null) {
        return value;
      }

      const handleOnReveal = () => {
        getCustomerWithdrawAddress(data.CustomerAddressID).then(res => {
          const firstRoutingInfo = res.data.at(0)?.RoutingInfo;
          routingInfoMap.set(data.CustomerAddressID, {
            WalletAddress: firstRoutingInfo?.WalletAddress ?? '* No Wallet Address *',
            Memo: firstRoutingInfo?.Memo ?? '* No Memo *',
            DestinationTag: firstRoutingInfo?.DestinationTag ?? '* No Destination Tag *',
            ExternalTxnURL: '',
          });
          api.refreshCells({ columns: ['WalletAddress', 'Memo'] });
        });
      };

      return (
        <Button
          data-testid="reveal-routing-info-button"
          startIcon={IconName.EyeShow}
          size={FormControlSizes.Small}
          onClick={handleOnReveal}
        >
          Reveal
        </Button>
      );
    }
  );

  const columns = useMemo(
    () => getCustomerAddressesColumns(RevealRoutingInfoRenderer, routingInfoMap),
    [RevealRoutingInfoRenderer, routingInfoMap]
  );

  const { getCustomContextMenuItems } = useConfirmCustomerAddressMenu({
    isAuthorizedToConfirm: isAuthorized(ACTION.DEALER_TRADING),
  });

  return (
    <EntityAdmin<ICustomerAddress, CustomerAddressesDrawerEntity>
      title="Customer Addresses"
      subtitle="View and manage customer deposit and withdrawal addresses."
      entityName="Customer Address"
      path="/customer-addresses"
      wsRequest={WS_REQUEST}
      entityIDField="CustomerAddressID"
      columns={columns}
      drawerOptions={drawerOptions}
      filterableProperties={filterableProperties}
      externallyControlledFilter={externallyControlledFilter}
      filterableServerFields={['Counterparty']}
      allowAddEntity={isAuthorized(ACTION.DEALER_TRADING)}
      allowEditEntity={isAuthorized(ACTION.DEALER_TRADING)}
      allowDeleteEntity={isAuthorized(ACTION.DEALER_TRADING)}
      persistKey="dealer-customer-addresses"
      getEntityForPatchPost={getEntityForPatchPost}
      getCustomContextMenuItems={getCustomContextMenuItems}
    />
  );
}
